import { useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../auth/msalConfig";
import { useTeamsContext } from "../auth/AuthProvider";
import { Box, Button, Typography, Stack } from "@mui/material";
import { AgentNetwork3D } from "../components/AgentNetwork3D";

export function LoginPage() {
  const { instance } = useMsal();
  const { isInTeams } = useTeamsContext();

  const inIframe = window.self !== window.top;

  const handleLogin = useCallback(() => {
    if (inIframe) {
      // Inside iframe (Teams tab, etc.): redirect is blocked, must use popup
      instance.loginPopup(loginRequest);
    } else {
      instance.loginRedirect(loginRequest);
    }
  }, [instance, inIframe]);

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        minHeight: "100vh",
        bgcolor: "#080c16",
        overflow: "hidden",
      }}
    >
      {/* 3D Enclosure Background — full viewport */}
      <AgentNetwork3D />

      {/* Gradient overlay — left side for readability */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 1,
          background: {
            xs: "linear-gradient(180deg, transparent 20%, rgba(8,12,22,0.85) 55%, rgba(8,12,22,0.95) 100%)",
            md: "linear-gradient(90deg, rgba(8,12,22,0.92) 0%, rgba(8,12,22,0.6) 35%, transparent 65%)",
          },
          pointerEvents: "none",
        }}
      />

      {/* Login Form — overlaid on the left, as if emerging from the enclosure */}
      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          display: "flex",
          alignItems: { xs: "flex-end", md: "center" },
          px: { xs: 3, md: "clamp(24px, 5vw, 80px)" },
          pb: { xs: 12, md: 0 },
          width: "100%",
          maxWidth: { md: 480 },
          pointerEvents: "auto",
        }}
      >
        <Box sx={{ maxWidth: 400 }}>
          {/* Altelix Logo */}
          <Box
            component="img"
            src="/altelix-logo.png"
            alt="Altelix"
            sx={{
              height: 72,
              width: "auto",
              objectFit: "contain",
              mb: 2.5,
              display: "block",
              // Login sits over the dark 3D scene regardless of theme — keep
              // the purple glow instead of the light-mode logo filter.
              filter: "drop-shadow(0 4px 16px rgba(108,92,231,0.35))",
            }}
          />

          <Typography
            variant="caption"
            sx={{
              display: "inline-block",
              px: 1.5,
              py: 0.5,
              mb: 2,
              borderRadius: 2,
              fontSize: "0.6rem",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "2.5px",
              color: "#a29bfe",
              background: "rgba(108,92,231,0.08)",
              border: "1px solid rgba(108,92,231,0.15)",
            }}
          >
            Financial Reconciliation
          </Typography>

          <Typography
            sx={{
              fontSize: "clamp(26px, 3.5vw, 42px)",
              fontWeight: 900,
              color: "#fff",
              lineHeight: 1.12,
              mb: 2,
              letterSpacing: "-0.5px",
            }}
          >
            Squad{" "}
            <Box
              component="span"
              sx={{
                background: "linear-gradient(135deg, #a29bfe, #55efc4)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Finance
            </Box>
          </Typography>

          <Typography
            variant="body2"
            sx={{ color: "#8b90a0", lineHeight: 1.8, mb: 4, maxWidth: 340 }}
          >
            5 AI agents working autonomously to reconcile transactions,
            collect invoices, and match financial records in real-time.
          </Typography>

          <Button
            variant="contained"
            size="large"
            onClick={handleLogin}
            sx={{
              py: 1.5,
              px: 4,
              fontSize: "0.9rem",
              fontWeight: 700,
              borderRadius: 2,
              background: "linear-gradient(135deg, #6c5ce7, #4834d4)",
              boxShadow: "0 4px 20px rgba(108,92,231,0.35)",
              "&:hover": {
                background: "linear-gradient(135deg, #a29bfe, #6c5ce7)",
                boxShadow: "0 6px 28px rgba(108,92,231,0.45)",
                transform: "translateY(-1px)",
              },
              transition: "all 0.25s ease",
            }}
          >
            <svg width="18" height="18" viewBox="0 0 21 21" fill="currentColor" style={{ marginRight: 10 }}>
              <rect x="1" y="1" width="9" height="9" />
              <rect x="11" y="1" width="9" height="9" />
              <rect x="1" y="11" width="9" height="9" />
              <rect x="11" y="11" width="9" height="9" />
            </svg>
            Sign in with Microsoft
          </Button>

          {/* Agent indicators */}
          <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
            {[
              { color: "#6c5ce7", label: "Retrieval" },
              { color: "#0984e3", label: "Import" },
              { color: "#00cec9", label: "Posting" },
              { color: "#fdcb6e", label: "Collector" },
              { color: "#55efc4", label: "Matching" },
            ].map((a) => (
              <Stack key={a.label} direction="row" alignItems="center" spacing={0.5}>
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: a.color,
                    boxShadow: `0 0 6px ${a.color}`,
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{ color: "#5a5e6e", fontSize: "0.6rem", letterSpacing: "0.5px" }}
                >
                  {a.label}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      </Box>

      {/* Bottom hint */}
      <Typography
        variant="caption"
        sx={{
          position: "absolute",
          bottom: 16,
          left: "50%",
          transform: "translateX(-50%)",
          color: "rgba(255,255,255,0.15)",
          fontSize: "0.6rem",
          letterSpacing: "1.5px",
          textTransform: "uppercase",
          zIndex: 2,
        }}
      >
        Drag to orbit &middot; Altelix &times; 4Clouders
      </Typography>
    </Box>
  );
}
